import { useState } from 'react';
import type { ModuleType } from '@flintmint/core-shared/firestore/schemas';
import { Button } from '@flintmint/web-shared/components/button';

import type { MedicalModuleInput } from '@/services/medical-modules-service';
import type { FlowWithId } from '@/services/flows-service';
import { DUPLICATE_MODULE_NAME_ERROR, isModuleNameTaken } from '../../module-name';
import { FlowChipsCell } from '../module-row/flow-chips-cell';

import styles from './add-module-row.module.scss';

const TYPE_LABELS: Record<ModuleType, string> = {
  complaint: 'Complaint',
  procedure: 'Procedure',
  other: 'Other',
};

interface AddModuleRowProps {
  availableTypes: ModuleType[];
  flows: FlowWithId[];
  moduleIdsByName: ReadonlyMap<string, readonly string[]>;
  onAdd: (data: MedicalModuleInput) => Promise<void>;
  onCancel: () => void;
  showType?: boolean;
}

export const AddModuleRow = ({ availableTypes, flows, moduleIdsByName, onAdd, onCancel, showType }: AddModuleRowProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [type, setType] = useState<ModuleType>(availableTypes[0]);
  const [name, setName] = useState('');
  const [criteria, setCriteria] = useState('');
  const [content, setContent] = useState('');
  const [flowIds, setFlowIds] = useState<string[]>([]);

  const isNameTaken = !isSaving && isModuleNameTaken(name, moduleIdsByName);
  const isValid = name.trim() !== '' && !isNameTaken;

  const handleAdd = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      await onAdd({ type, name: name.trim(), content, criteria: criteria.trim(), flowIds, enabled: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.row} data-editing data-adding data-has-type={showType || undefined} data-testid="module-add-row">
      <span />
      <div className={styles.field}>
        <input
          className={styles.nameInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (e.g. Sepsis)"
          aria-invalid={isNameTaken}
          data-testid="module-name-input"
        />
        {isNameTaken && (
          <p className={styles.fieldError} data-testid="module-name-error">
            {DUPLICATE_MODULE_NAME_ERROR}
          </p>
        )}
      </div>
      {showType && (
        <select
          className={styles.typeSelect}
          value={type}
          onChange={(e) => setType(e.target.value as ModuleType)}
          data-testid="module-type-select"
        >
          {availableTypes.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      )}
      <textarea
        className={styles.criteriaTextarea}
        value={criteria}
        onChange={(e) => setCriteria(e.target.value)}
        placeholder="Criteria (e.g. infection with SIRS)"
        rows={2}
        data-testid="module-criteria-input"
      />
      <textarea
        className={styles.contentTextarea}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Content"
        rows={3}
        data-testid="module-content-input"
      />
      <FlowChipsCell flowIds={flowIds} flows={flows} onChange={setFlowIds} />
      <span />
      <span />
      <div className={styles.actions}>
        <Button
          size="small"
          color="success"
          onClick={handleAdd}
          loading={isSaving}
          disabled={!isValid}
          data-testid="module-add-confirm-btn"
        >
          Add
        </Button>
        <Button size="small" appearance="clear" onClick={onCancel} disabled={isSaving} data-testid="module-add-cancel-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
};
